import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { api, fmtDate } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";
import { PageHeader, EmptyState, RefBadge } from "@/components/shared";

/**
 * Global Estimations page — reachable from the top quick-access strip.
 * Lists every estimation dossier (each attached to a property) and lets
 * Julia start a new one; both deep-link into the property's Estimation tab.
 */
export default function Estimations() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [properties, setProperties] = useState([]);

  const load = useCallback(async () => {
    const [d, p] = await Promise.all([api.get("/estimation_dossiers"), api.get("/properties")]);
    setDossiers(d.data);
    setProperties(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  const propById = Object.fromEntries(properties.map((p) => [p.id, p]));
  const withoutDossier = properties.filter((p) => !dossiers.some((d) => d.property_id === p.id));
  const openDossier = (pid) => navigate(`/properties?open=${pid}&tab=dossier`);

  const sorted = [...dossiers].sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));

  return (
    <div data-testid="estimations-page">
      <PageHeader
        title={t("estimationsTitle")}
        subtitle={t("estimationsSubtitle")}
        action={
          withoutDossier.length > 0 && (
            <Select onValueChange={openDossier}>
              <SelectTrigger className="w-56 bg-slate-900 text-white border-slate-900 hover:bg-slate-800 [&>svg]:text-white" data-testid="start-estimation-select">
                <SelectValue placeholder={t("startEstimation")} />
              </SelectTrigger>
              <SelectContent>
                {withoutDossier.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.ref_code ? `${p.ref_code} · ` : ""}{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
      />

      {sorted.length === 0 ? (
        <EmptyState message={t("noEstimations")} />
      ) : (
        <ul className="divide-y divide-stone-100 bg-white border border-stone-200 rounded-xl overflow-hidden">
          {sorted.map((d) => {
            const prop = propById[d.property_id];
            return (
              <li key={d.id}>
                <button
                  data-testid={`estimation-row-${d.id}`}
                  onClick={() => openDossier(d.property_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 flex-shrink-0">
                    <ClipboardList size={16} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate flex items-center gap-2">
                      {prop?.ref_code && <RefBadge code={prop.ref_code} />}
                      {prop?.name || d.property_name || "—"}
                    </p>
                    <p className="text-[11px] text-stone-400 mt-0.5">
                      {d.date ? fmtDate(d.date, lang) : ""}
                      {d.consultant ? ` · ${d.consultant}` : ""}
                    </p>
                  </div>
                  {(d.updated_at || d.created_at) && (
                    <span className="text-[11px] text-stone-400 whitespace-nowrap">
                      {fmtDate((d.updated_at || d.created_at).slice(0, 10), lang)}
                    </span>
                  )}
                  <ChevronRight size={15} strokeWidth={1.5} className="text-stone-300 flex-shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
